// 레이드 보스 '잿불 비룡' 절차 로우폴리 비룡(대체판). 평소에는 전용 GLB(wyvern.ts)를 그리고, GLB를 읽는 동안·읽기 실패·?wyvernproc 비교 때만 이 모델을 쓴다.
// 몸통·목·머리·턱·날개(위팔·아래팔 막)·뒷다리·꼬리 6마디를 그룹 계층으로 만들고, 상태·패턴·비행 높이로 목표 자세를 정해
// 지수 감쇠로 따라간다(프레임 루프 할당 없음). 용암 균열·눈·목구멍은 블룸 문턱을 넘는 자체 발광, 브레스는 가산 불꽃 원뿔.
// 드로우콜: 몸(병합 1 + 외곽선 1) ×(몸통·목·머리·턱·꼬리 마디·다리·날개) — 보스 한 마리라 약 40. 그림자는 몸통·날개만 드리운다.
import * as THREE from 'three/webgpu';
import { abs, float, floor, fract, mix, screenCoordinate, sin, smoothstep, uniform, uv, vec3 } from 'three/tsl';
import { WYVERN, WyvernPattern } from '../../shared/content/raid';
import { BossState, type GameView } from '../../shared/types';
import type { GameClocks } from '../clock';
import { colored, merge as mergeIndexed } from '../geo';
import { addHull, charMaterial, hullMaterial, RimControl } from '../materials';
import { angleDelta, clamp01, damp, easeIn, easeOut, lerp, phaseOf } from '../util';

/** 렌더 배율(설계 길이 약 10m·날개 편 폭 약 15m → ×1.1). 판정 반경(WYVERN.radius)은 바꾸지 않는다 */
const SCALE = 1.1;
/** 비행 높이(m, 공중 1.0일 때 몸 높이) */
const FLY_H = WYVERN.flight.height + 1.5;
const COL = { hide: 0x2b2120, dark: 0x1b1414, belly: 0x7a3d22, horn: 0xd8c7a8, membrane: 0x6a2216, membraneEdge: 0x3a120c, claw: 0xe8dcc4 } as const;
const TAIL_N = 6;

interface Pose {
  h: number; pitch: number; roll: number; yaw: number;
  neck: number; head: number; jaw: number;
  fold: number; elbow: number; flap: number; flapAmp: number; flapRate: number;
  leg: number; legRate: number; tuck: number;
  tailYaw: number; tailSway: number; tailLift: number;
  glow: number; throat: number; flame: number; sy: number;
}

/** 자세 값 이름(프레임마다 Object.keys로 새 배열을 만들지 않게 고정) */
const POSE_KEYS: readonly (keyof Pose)[] = ['h', 'pitch', 'roll', 'yaw', 'neck', 'head', 'jaw', 'fold', 'elbow', 'flap', 'flapAmp', 'flapRate', 'leg', 'legRate', 'tuck', 'tailYaw', 'tailSway', 'tailLift', 'glow', 'throat', 'flame', 'sy'];
function pose(): Pose { return { ...POSE_REST }; }
function copyPose(dst: Pose, src: Readonly<Pose>): void { for (let i = 0; i < POSE_KEYS.length; i++) dst[POSE_KEYS[i]] = src[POSE_KEYS[i]]; }
/** 쉼 자세(목표 자세를 프레임마다 이 값으로 되돌린다 — 새 객체 없이) */
const POSE_REST: Readonly<Pose> = { h: 0, pitch: 0, roll: 0, yaw: 0, neck: -0.55, head: 0.45, jaw: 0, fold: 1.15, elbow: -1.9, flap: 0.25, flapAmp: 0, flapRate: 0, leg: 0, legRate: 0, tuck: 0, tailYaw: 0, tailSway: 0.12, tailLift: 0, glow: 0.8, throat: 0, flame: 0, sy: 1 };

/** 조각 병합: 다면체(색인 없음)와 원기둥·상자(색인 있음)가 섞이므로 모두 색인 없이 맞춘다 */
function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  return mergeIndexed(parts.map((g) => { if (!g.index) return g; const n = g.toNonIndexed(); g.dispose(); return n; }));
}

/** 원기둥을 +Z(길이 len)로 눕혀 z0에서 시작하게 만든다 */
function limbZ(r0: number, r1: number, len: number, seg: number, hex: number, z0 = 0, y = 0, x = 0): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r1, r0, len, seg);
  g.rotateX(Math.PI / 2);
  return colored(g, hex, x, y, z0 + len / 2);
}

/** 원기둥을 아래(-Y)로 len만큼 */
function limbDown(r0: number, r1: number, len: number, hex: number, x = 0, z = 0): THREE.BufferGeometry {
  return colored(new THREE.CylinderGeometry(r0, r1, len, 6), hex, x, -len / 2, z);
}

/** 원기둥을 옆(side·+X)으로 len만큼 */
function limbSide(side: number, r0: number, r1: number, len: number, hex: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r1, r0, len, 6);
  g.rotateZ(-side * Math.PI / 2);
  return colored(g, hex, side * len / 2, 0, 0);
}

/** 날개막: 점 목록을 부채꼴로 잇는다(양면). 첫 점이 중심 */
function membrane(pts: readonly (readonly [number, number, number])[], side: number, hexIn: number, hexOut: number): THREE.BufferGeometry {
  const pos: number[] = [], col: number[] = [];
  const ci = new THREE.Color(hexIn), co = new THREE.Color(hexOut);
  for (let i = 1; i < pts.length - 1; i++) {
    for (const [k, p] of [[0, pts[0]], [1, pts[i]], [2, pts[i + 1]]] as const) {
      pos.push(side * p[0], p[1], p[2]);
      const c = k === 0 ? ci : co;
      col.push(c.r, c.g, c.b);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.computeVertexNormals();
  // merge 짝을 맞추려 uv를 넣는다(원기둥·구는 uv가 있다)
  g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
  return g;
}

/** 등 가시·뿔: 원뿔(꼭짓점 dir 방향) */
function spike(r: number, h: number, hex: number, x: number, y: number, z: number, tiltX = 0, tiltZ = 0): THREE.BufferGeometry {
  const g = new THREE.ConeGeometry(r, h, 5);
  g.translate(0, h / 2, 0);
  g.rotateX(tiltX); g.rotateZ(tiltZ);
  return colored(g, hex, x, y, z);
}

/** 용암 균열: 가는 상자(자체 발광 재질로 따로 병합) */
function crack(w: number, h: number, d: number, x: number, y: number, z: number, ry = 0, rz = 0): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  g.rotateZ(rz); g.rotateY(ry);
  return colored(g, 0xffffff, x, y, z);
}

export class WyvernProcVisual {
  readonly root = new THREE.Group();
  readonly rim = new RimControl(0xffb070, 0.32);
  x = 0; z = 0;
  /** 공중 높이(m, 그림자·카메라용) */
  height = 0;

  private readonly body = new THREE.Group();
  private readonly neck = new THREE.Group();
  private readonly head = new THREE.Group();
  private readonly jaw = new THREE.Group();
  private readonly wings: { sh: THREE.Group; el: THREE.Group; side: number }[] = [];
  private readonly legs: { hip: THREE.Group; knee: THREE.Group; side: number }[] = [];
  private readonly tail: THREE.Group[] = [];
  private readonly flame: THREE.Mesh;
  private readonly throatMesh: THREE.Mesh;
  private readonly geos: THREE.BufferGeometry[] = [];
  private readonly mats: THREE.Material[] = [];
  private readonly skinMats: THREE.MeshStandardNodeMaterial[] = [];
  private readonly hullMat = hullMaterial(0.06);
  private readonly uGlow = uniform(0.8);
  private readonly uGlowCol = uniform(new THREE.Color(1.0, 0.36, 0.06));
  private readonly uThroat = uniform(0);
  private readonly uTime = uniform(0);
  private readonly uFlame = uniform(0);
  private readonly fade = uniform(0);
  private fadeV = 0;
  private readonly cur = pose();
  private readonly tgt = pose();
  private flapPh = 0;
  private walkPh = 0;
  private swayPh = 0;
  /** 지상 공격 패턴이 끝난 뒤(친 뒤 상태)에도 어떤 패턴이었는지 기억한다 */
  private lastPat = -1;
  private lastState = -1;
  private sx = 0; private sz = 0; private yaw = 0;
  private inited = false;

  constructor() {
    const hide = charMaterial(0xffffff, this.rim, { vertexColors: true, roughness: 0.85 });
    hide.flatShading = true;
    const wingMat = charMaterial(0xffffff, this.rim, { vertexColors: true, roughness: 0.9 });
    wingMat.flatShading = true;
    wingMat.side = THREE.DoubleSide;
    this.skinMats.push(hide, wingMat);
    // 영웅이 몸 뒤로 들어가면 1px 체크무늬로 비춘다(고목 수호자와 같은 방식)
    const checker = floor(screenCoordinate.x).add(floor(screenCoordinate.y)).mod(2);
    for (const mt of [hide, wingMat, this.hullMat] as THREE.NodeMaterial[]) { mt.opacityNode = mix(float(1), checker, this.fade); mt.alphaTest = 0.5; }
    // 용암 균열: 맥동하는 주황(블룸 문턱 위)
    const glowMat = new THREE.MeshBasicNodeMaterial();
    glowMat.colorNode = this.uGlowCol.mul(this.uGlow.mul(float(1.6).add(sin(this.uTime.mul(3.1)).mul(0.35))));
    const eyeMat = new THREE.MeshBasicNodeMaterial();
    eyeMat.colorNode = vec3(2.4, 1.5, 0.35).mul(this.uGlow.mul(0.5).add(0.6));
    const throatMat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    throatMat.colorNode = vec3(2.6, 1.3, 0.3).mul(this.uThroat);
    // 브레스 불꽃: 원뿔 uv로 흐르는 줄무늬 + 끝으로 갈수록 옅게
    const flameMat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    const fv = uv();
    const streak = abs(fract(fv.y.mul(3).sub(this.uTime.mul(2.4)).add(sin(fv.x.mul(18.85)).mul(0.12))).sub(0.5)).mul(2);
    const along = smoothstep(0, 0.12, float(1).sub(fv.y)).mul(smoothstep(0, 0.5, fv.y));
    flameMat.colorNode = mix(vec3(2.0, 0.6, 0.08), vec3(2.2, 1.5, 0.5), fv.y);
    flameMat.opacityNode = along.mul(float(0.55).add(streak.mul(0.45))).mul(this.uFlame);
    this.mats.push(hide, wingMat, glowMat, eyeMat, throatMat, flameMat, this.hullMat);

    const add = (parent: THREE.Object3D, geo: THREE.BufferGeometry, mat: THREE.Material, outline = true, shadow = false): THREE.Mesh => {
      const m = new THREE.Mesh(geo, mat);
      m.castShadow = shadow;
      if (outline) addHull(m, this.hullMat);
      parent.add(m);
      this.geos.push(geo);
      return m;
    };

    // ── 몸통(뒷다리 엉덩이 높이 2.0, 가슴 쪽이 조금 높다)
    const torso = new THREE.IcosahedronGeometry(1, 1);
    torso.scale(1.35, 1.15, 2.3);
    const chest = new THREE.IcosahedronGeometry(1, 1);
    chest.scale(1.15, 1.05, 1.1);
    const bellyG = new THREE.IcosahedronGeometry(1, 1);
    bellyG.scale(1.0, 0.7, 1.9);
    const torsoParts: THREE.BufferGeometry[] = [
      colored(torso, COL.hide, 0, 2.7, -0.2),
      colored(chest, COL.hide, 0, 3.05, 1.3),
      colored(bellyG, COL.belly, 0, 2.15, 0.2),
    ];
    for (let i = 0; i < 7; i++) torsoParts.push(spike(0.22 - i * 0.012, 0.75 - i * 0.05, COL.dark, 0, 3.75 - i * 0.07, 1.5 - i * 0.62, -0.5));
    add(this.body, merge(torsoParts), hide, true, true);
    add(this.body, merge([
      crack(0.06, 0.9, 0.05, 1.28, 2.75, 0.4, 0, 0.4), crack(0.06, 0.7, 0.05, 1.22, 2.5, -0.6, 0, -0.3), crack(0.05, 0.6, 0.05, 1.05, 2.95, -1.4, 0, 0.6),
      crack(0.06, 0.9, 0.05, -1.28, 2.75, 0.3, 0, -0.4), crack(0.06, 0.7, 0.05, -1.22, 2.5, -0.7, 0, 0.3), crack(0.05, 0.6, 0.05, -1.05, 2.95, -1.5, 0, -0.6),
      crack(0.8, 0.05, 0.05, 0, 3.9, 0.2, 0, 0), crack(0.6, 0.05, 0.05, 0, 3.75, -0.9, 0.3, 0),
    ]), glowMat, false);

    // ── 목(가슴 앞 위) → 머리 → 턱
    this.neck.position.set(0, 3.25, 1.95);
    const neckParts = [limbZ(0.62, 0.42, 2.3, 7, COL.hide)];
    for (let i = 0; i < 3; i++) neckParts.push(spike(0.14, 0.45, COL.dark, 0, 0.45 - i * 0.04, 0.4 + i * 0.7, -0.6));
    neckParts.push(colored(new THREE.BoxGeometry(0.55, 0.25, 2.0), COL.belly, 0, -0.33, 1.1));
    add(this.neck, merge(neckParts), hide);
    add(this.neck, merge([crack(0.05, 0.05, 1.4, 0.45, 0.05, 1.1), crack(0.05, 0.05, 1.4, -0.45, 0.05, 1.1)]), glowMat, false);
    this.head.position.set(0, 0, 2.35);
    const skull = new THREE.BoxGeometry(0.95, 0.62, 1.1);
    const snout = new THREE.BoxGeometry(0.66, 0.42, 1.0);
    add(this.head, merge([
      colored(skull, COL.hide, 0, 0.12, 0.35),
      colored(snout, COL.hide, 0, 0.05, 1.25),
      spike(0.13, 0.95, COL.horn, 0.32, 0.38, -0.05, -1.95, -0.25),
      spike(0.13, 0.95, COL.horn, -0.32, 0.38, -0.05, -1.95, 0.25),
      spike(0.08, 0.45, COL.horn, 0.46, 0.15, 0.2, -1.7, -0.9),
      spike(0.08, 0.45, COL.horn, -0.46, 0.15, 0.2, -1.7, 0.9),
      spike(0.07, 0.22, COL.horn, 0, 0.27, 1.6, -0.3),
    ]), hide);
    const eyeL = new THREE.BoxGeometry(0.2, 0.08, 0.16); eyeL.rotateZ(0.3); eyeL.translate(-0.42, 0.24, 0.72);
    const eyeR = new THREE.BoxGeometry(0.2, 0.08, 0.16); eyeR.rotateZ(-0.3); eyeR.translate(0.42, 0.24, 0.72);
    add(this.head, merge([colored(eyeL, 0xffffff), colored(eyeR, 0xffffff)]), eyeMat, false);
    this.jaw.position.set(0, -0.18, 0.0);
    const teeth: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 4; i++) for (const s of [-1, 1]) teeth.push(spike(0.04, 0.16, COL.claw, s * 0.25, 0.04, 0.9 + i * 0.28));
    add(this.jaw, merge([colored(new THREE.BoxGeometry(0.7, 0.2, 1.75), COL.dark, 0, -0.06, 0.95), ...teeth]), hide);
    this.throatMesh = add(this.head, colored(new THREE.SphereGeometry(0.3, 8, 6), 0xffffff, 0, -0.08, 1.15), throatMat, false);
    this.throatMesh.renderOrder = 5;
    this.head.add(this.jaw);
    this.neck.add(this.head);
    // 브레스: 입에서 +Z로 뻗는 원뿔(길이 1 기준, 키운다)
    const flameGeo = new THREE.CylinderGeometry(0.9, 0.12, 1, 10, 1, true);
    flameGeo.rotateX(-Math.PI / 2); flameGeo.translate(0, 0, 0.5);
    this.flame = new THREE.Mesh(flameGeo, flameMat);
    this.flame.position.set(0, -0.1, 1.7);
    this.flame.renderOrder = 6;
    this.flame.visible = false;
    this.flame.frustumCulled = false;
    this.geos.push(flameGeo);
    this.head.add(this.flame);
    this.body.add(this.neck);

    // ── 날개: 어깨(위팔 3.1) → 팔꿈치(아래팔·손가락 3.3), 막은 위팔 판·아래팔 판 둘
    for (const side of [1, -1]) {
      const sh = new THREE.Group();
      sh.position.set(side * 1.0, 3.55, 0.9);
      add(sh, merge([limbSide(side, 0.24, 0.16, 3.1, COL.dark), spike(0.12, 0.5, COL.claw, side * 3.1, 0.05, 0, 0, -side * 1.2)]), hide, true, true);
      add(sh, membrane([[0.2, 0, -0.1], [3.1, 0.05, 0], [3.0, 0, -2.6], [1.6, 0, -2.9], [0.3, -0.05, -2.4]], side, COL.membrane, COL.membraneEdge), wingMat, false, true);
      const el = new THREE.Group();
      el.position.set(side * 3.1, 0, 0);
      add(el, merge([
        limbSide(side, 0.15, 0.09, 3.4, COL.dark),
        // 손가락 뼈 두 개(막의 살)
        (() => { const g = new THREE.CylinderGeometry(0.05, 0.08, 2.9, 5); g.rotateZ(-side * Math.PI / 2); g.rotateY(side * 0.75); return colored(g, COL.dark, side * 1.05, 0, -1.0); })(),
        (() => { const g = new THREE.CylinderGeometry(0.05, 0.08, 2.6, 5); g.rotateZ(-side * Math.PI / 2); g.rotateY(side * 1.25); return colored(g, COL.dark, side * 0.42, 0, -1.25); })(),
      ]), hide, true, true);
      add(el, membrane([[0, 0, -0.1], [3.4, 0.05, -0.2], [2.9, 0, -1.6], [2.2, 0, -2.0], [1.5, 0, -2.6], [0.8, 0, -2.4], [0, 0, -2.6]], side, COL.membrane, COL.membraneEdge), wingMat, false, true);
      sh.add(el);
      this.body.add(sh);
      this.wings.push({ sh, el, side });
    }

    // ── 뒷다리: 엉덩이(넓적다리 1.15) → 무릎(정강이 1.0 + 발)
    for (const side of [1, -1]) {
      const hip = new THREE.Group();
      hip.position.set(side * 0.95, 2.05, -0.55);
      add(hip, merge([colored(new THREE.IcosahedronGeometry(0.55, 0).scale(0.9, 1.1, 1.1), COL.hide, 0, -0.2, 0), limbDown(0.4, 0.28, 1.15, COL.hide, 0, 0.2)]), hide);
      const knee = new THREE.Group();
      knee.position.set(0, -1.1, 0.35);
      const claws: THREE.BufferGeometry[] = [];
      for (let k = -1; k <= 1; k++) claws.push(spike(0.06, 0.32, COL.claw, k * 0.18, -0.95, 0.42, 1.45));
      add(knee, merge([limbDown(0.25, 0.17, 0.95, COL.hide, 0, -0.2), colored(new THREE.BoxGeometry(0.5, 0.16, 0.7), COL.dark, 0, -0.92, 0.1), ...claws]), hide);
      hip.add(knee);
      this.body.add(hip);
      this.legs.push({ hip, knee, side });
    }

    // ── 꼬리 6마디(끝에 가시 삽날)
    let parent: THREE.Object3D = this.body;
    for (let i = 0; i < TAIL_N; i++) {
      const seg = new THREE.Group();
      const len = 1.15 - i * 0.05;
      seg.position.set(0, i === 0 ? 2.65 : 0, i === 0 ? -2.3 : -(1.15 - (i - 1) * 0.05));
      const r0 = 0.6 - i * 0.085, r1 = 0.6 - (i + 1) * 0.085;
      const g = new THREE.CylinderGeometry(Math.max(0.09, r1), Math.max(0.12, r0), len, 6);
      g.rotateX(-Math.PI / 2);
      const parts = [colored(g, COL.hide, 0, 0, -len / 2), spike(Math.max(0.06, r0 * 0.32), 0.25 + r0 * 0.5, COL.dark, 0, r0 * 0.85, -len * 0.4, -0.7)];
      if (i === TAIL_N - 1) {
        const spade = new THREE.ConeGeometry(0.55, 1.1, 4);
        spade.rotateX(-Math.PI / 2); spade.scale(1, 0.25, 1);
        parts.push(colored(spade, COL.horn, 0, 0, -len - 0.45));
      }
      add(seg, merge(parts), hide, true, i < 2);
      if (i < 2) add(seg, crack(0.05, 0.05, len * 0.8, 0, r0 * 0.6, -len / 2), glowMat, false);
      parent.add(seg);
      parent = seg;
      this.tail.push(seg);
    }

    this.root.add(this.body);
    this.root.scale.setScalar(SCALE);
    this.root.visible = false;
  }

  /** 임시 브레스 원뿔을 그릴지(VFX 레인이 따로 그리면 끈다) */
  breathCone = true;
  /** 입 위치(월드). 원뿔이 머리에 붙어 있으므로 원뿔 시작점을 쓴다 */
  mouthWorld(out: THREE.Vector3): boolean {
    if (!this.root.visible) return false;
    this.flame.getWorldPosition(out);
    return true;
  }
  /** 입이 향한 방향(월드, 단위 벡터) */
  mouthDir(out: THREE.Vector3): boolean {
    if (!this.root.visible) return false;
    this.flame.getWorldDirection(out);
    return true;
  }

  reset(): void { this.inited = false; this.lastPat = -1; this.lastState = -1; this.flame.visible = false; }

  /** 목표 자세 정하기 */
  private plan(view: GameView, t: number): void {
    const b = view.boss, r = view.raid, P = this.tgt, st = b.state;
    const air = typeof b.air === 'number' ? b.air : 0;
    const pat = r ? r.pattern : -1;
    if (pat >= 0 && pat !== WyvernPattern.phase2 && pat !== WyvernPattern.enrage) this.lastPat = pat;
    copyPose(P, POSE_REST);
    P.glow = (b.phase2 ? 1.25 : 0.85) * (r && r.enraged ? 1.45 : 1);
    switch (st) {
      case BossState.Intro: {
        // 하늘에서 날개를 치며 내려앉아 포효한다
        const p = clamp01(t / WYVERN.intro);
        const down = clamp01(p / 0.62);
        P.h = FLY_H * 1.3 * (1 - easeOut(down));
        P.fold = 0; P.elbow = 0; P.flap = 0.1; P.flapAmp = down < 1 ? 0.75 : 0; P.flapRate = 0.24;
        P.tuck = 1 - down;
        if (down >= 1) {
          const roar = clamp01((p - 0.66) / 0.3);
          P.neck = lerp(-0.55, -1.0, easeOut(roar)); P.head = lerp(0.45, -0.25, easeOut(roar)); P.jaw = roar > 0 ? 0.75 : 0;
          P.fold = lerp(0, 0.4, roar); P.flap = 0.55; P.pitch = -0.12 * roar;
        }
        P.glow *= 0.4 + 0.6 * p;
        break;
      }
      case BossState.Chase: {
        P.leg = 0.38; P.legRate = 0.13; P.tailSway = 0.22;
        break;
      }
      case BossState.SlamWindup: {
        // 물기 준비: 목을 뒤로 젖히고 입을 연다
        const p = easeOut(clamp01(t / WYVERN.bite.windup));
        P.neck = lerp(-0.55, -1.15, p); P.head = lerp(0.45, 0.9, p); P.jaw = 0.65 * p; P.pitch = -0.08 * p;
        P.fold = 0.9; P.flap = 0.4;
        break;
      }
      case BossState.SweepWindup: {
        if (this.lastPat === WyvernPattern.charge) {
          // 돌진 준비: 몸을 낮추고 날개를 뒤로 펼친다
          const p = easeOut(clamp01(t / WYVERN.charge.windup));
          P.pitch = 0.16 * p; P.sy = 1 - 0.1 * p; P.neck = lerp(-0.55, -0.1, p); P.head = lerp(0.45, 0.15, p);
          P.fold = lerp(1.15, 0.55, p); P.elbow = lerp(-1.9, -0.6, p); P.flap = 0.55; P.jaw = 0.3 * p;
          P.tailLift = 0.25 * p;
        } else {
          // 꼬리 준비: 몸을 비틀어 꼬리를 감아 둔다
          const p = easeOut(clamp01(t / WYVERN.tail.windup));
          P.yaw = 0.55 * p; P.tailYaw = -0.5 * p; P.tailLift = 0.3 * p; P.tailSway = 0.04;
          P.roll = 0.06 * p;
        }
        break;
      }
      case BossState.Cast: {
        if (this.lastPat === WyvernPattern.takeoff) {
          P.fold = 0; P.elbow = 0; P.flap = 0.05; P.flapAmp = 0.85; P.flapRate = 0.3; P.tuck = clamp01(air * 2);
          P.pitch = -0.18; P.neck = -0.75; P.head = 0.4;
        } else {
          // 화염 장판: 몸을 세우고 목구멍을 달군 뒤 고개를 내리꽂아 뱉는다
          const p = clamp01(t / WYVERN.pools.windup);
          const spit = clamp01((p - 0.78) / 0.22);
          P.pitch = lerp(0, -0.3, easeOut(Math.min(1, p * 2))) + 0.25 * spit;
          P.neck = lerp(-0.55, -1.25, easeOut(Math.min(1, p * 1.6))) + 0.9 * spit;
          P.head = lerp(0.45, 0.2, p) + 0.5 * spit;
          P.jaw = p > 0.5 ? 0.55 : 0.15;
          P.throat = easeIn(p);
          P.fold = 0.7; P.elbow = -1.2; P.flap = 0.6;
        }
        break;
      }
      case BossState.Slam: {
        if (this.lastPat === WyvernPattern.charge) {
          // 돌진: 날개를 뒤로 젖히고 내달린다
          P.pitch = 0.2; P.neck = -0.05; P.head = 0.1; P.jaw = 0.4;
          P.fold = 0.5; P.elbow = -0.4; P.flap = 0.5; P.leg = 0.75; P.legRate = 0.42; P.tailLift = 0.3;
        } else if (this.lastPat === WyvernPattern.land) {
          // 착지 충격: 눌렸다가 일어선다
          const p = clamp01(t / 21);
          P.sy = 1 - 0.14 * Math.sin(p * Math.PI); P.fold = lerp(0, 0.8, p); P.elbow = lerp(0, -1.5, p); P.flap = 0.65;
          P.neck = -0.3; P.head = 0.4; P.jaw = 0.4 * (1 - p);
        } else {
          // 물기: 목을 내리꽂으며 턱을 닫는다
          const p = clamp01(t / 10);
          P.neck = lerp(-1.15, 0.05, easeOut(p)); P.head = lerp(0.9, 0.25, easeOut(p)); P.jaw = lerp(0.7, 0.0, easeIn(p));
          P.pitch = 0.1 * p; P.fold = 0.9; P.flap = 0.4;
        }
        break;
      }
      case BossState.Sweep: {
        if (this.lastPat === WyvernPattern.pools) {
          P.neck = -0.2; P.head = 0.6; P.jaw = 0.3; P.throat = 0.3 * (1 - clamp01(t / 20));
        } else {
          // 꼬리 휘두르기: 몸을 반대로 돌리며 꼬리가 크게 쓸고 지나간다
          const p = easeOut(clamp01(t / 14));
          P.yaw = lerp(0.55, -0.7, p); P.tailYaw = lerp(-0.5, 0.75, p); P.tailLift = 0.15; P.tailSway = 0.02; P.roll = -0.05;
        }
        break;
      }
      case BossState.Stagger: {
        // 경직: 고개를 떨구고 휘청인다(균열이 약해진다)
        P.neck = 0.25 + Math.sin(t * 0.21) * 0.06; P.head = 0.55; P.jaw = 0.25; P.pitch = 0.12; P.roll = Math.sin(t * 0.13) * 0.1;
        P.fold = 0.6; P.elbow = -0.9; P.flap = -0.15; P.glow *= 0.6;
        break;
      }
      case BossState.Dying: {
        const p = clamp01(t / WYVERN.dying);
        P.roll = 1.25 * easeIn(p); P.h = -0.5 * p; P.neck = lerp(-0.4, 0.5, p); P.head = 0.6; P.jaw = 0.5;
        P.fold = 0.3; P.elbow = -0.6; P.flap = lerp(0.2, -0.5, p); P.tailSway = 0; P.glow *= 1 - p * 0.9;
        break;
      }
      default: break;
    }
    // 공중(이륙 막바지·비행·착지): 몸 높이와 날갯짓은 공중 값이 정한다
    if (air > 0 && st !== BossState.Dying) {
      P.h = Math.max(P.h, FLY_H * air);
      P.fold = 0; P.elbow = 0; P.tuck = clamp01(air * 2); P.leg = 0;
      P.flapAmp = Math.max(P.flapAmp, 0.8); P.flapRate = Math.max(P.flapRate, 0.2); P.flap = 0.05;
      if (pat === WyvernPattern.breath && this.lastState === BossState.Cast) {
        // 브레스: 브레스 줄마다 경고가 찰 무렵 고개를 숙이고 불을 뿜는다(stepFlight 2단계는 stateTick 0부터 센다)
        const F = WYVERN.flight;
        let fire = 0, aim = 0;
        for (let k = 0; k < F.breaths; k++) {
          const s0 = k * F.breathGap + F.breathWindup - 34, s1 = k * F.breathGap + F.breathWindup + 10;
          if (t >= s0 - 30 && t < s1) aim = Math.max(aim, clamp01((t - (s0 - 30)) / 30));
          if (t >= s0 && t < s1) fire = Math.max(fire, Math.min(1, (t - s0) / 6, (s1 - t) / 8));
        }
        P.neck = lerp(-0.4, 0.55, aim); P.head = lerp(0.3, 0.75, aim); P.jaw = Math.max(0.15, aim * 0.8);
        P.throat = Math.max(aim * 0.7, fire); P.flame = fire; P.pitch = 0.18 * aim;
      } else if (pat === WyvernPattern.land) {
        P.flapAmp = 0.5; P.flapRate = 0.16; P.pitch = -0.22; P.tuck = clamp01(air * 1.5); P.neck = -0.8; P.head = 0.6; P.jaw = 0.45;
      }
    }
  }

  update(view: GameView, alpha: number, dt: number, clocks: GameClocks): void {
    const b = view.boss, st = b.state;
    const show = st !== BossState.Inactive && st !== BossState.Dead;
    this.root.visible = show;
    if (!show) { this.reset(); return; }
    if (st !== this.lastState) {
      // 친 뒤 상태로 넘어갈 때만 패턴 기억을 이어 간다(쫓기·경직에서 지운다)
      if (st === BossState.Chase || st === BossState.Stagger) this.lastPat = -1;
      this.lastState = st;
    }
    const t = phaseOf(b.stateTick, clocks.worldRate, alpha);
    this.plan(view, t);
    const tx = b.prevX + (b.x - b.prevX) * alpha, tz = b.prevZ + (b.z - b.prevZ) * alpha;
    const air = typeof b.air === 'number' ? b.air : 0;
    if (!this.inited) {
      this.inited = true;
      this.sx = tx; this.sz = tz; this.yaw = b.facing;
      copyPose(this.cur, this.tgt);
    }
    // 공중에서는 게임 위치가 브레스 줄 끝으로 뛰므로 몸이 미끄러지듯 따라간다(지상은 그대로)
    const follow = air > 0.02 ? damp(2.6, dt) : 1;
    this.sx += (tx - this.sx) * follow; this.sz += (tz - this.sz) * follow;
    this.yaw += angleDelta(this.yaw, b.facing) * (air > 0.02 ? damp(3, dt) : damp(14, dt));
    this.x = this.sx; this.z = this.sz;

    const C = this.cur, P = this.tgt;
    const k = damp(9, dt), kf = damp(5, dt);
    for (let i = 0; i < POSE_KEYS.length; i++) {
      const key = POSE_KEYS[i];
      const kk = key === 'h' || key === 'flapAmp' || key === 'tuck' ? kf : key === 'jaw' || key === 'flame' ? damp(18, dt) : k;
      C[key] += (P[key] - C[key]) * kk;
    }
    const wdt = clocks.worldDelta;
    this.flapPh += C.flapRate * wdt * Math.PI * 2 / 6;
    this.walkPh += C.legRate * wdt;
    this.swayPh += wdt * 0.05;
    this.height = C.h * SCALE;

    const flap = C.flapAmp > 0.01 ? Math.sin(this.flapPh) * C.flapAmp : 0;
    const bob = C.flapAmp > 0.01 ? -Math.sin(this.flapPh + 0.6) * 0.25 * C.flapAmp : (C.legRate > 0 ? Math.abs(Math.sin(this.walkPh)) * 0.08 : Math.sin(this.swayPh * 0.6) * 0.03);
    this.root.position.set(this.sx, 0, this.sz);
    this.root.rotation.y = this.yaw;
    this.body.position.y = C.h + bob;
    this.body.rotation.set(C.pitch, C.yaw, C.roll);
    this.body.scale.set(1, C.sy, 1);
    // 숨쉬기(쉬는 동안 목이 살짝 오르내린다)
    const breathe = Math.sin(this.swayPh * 0.9) * 0.04;
    this.neck.rotation.set(C.neck + breathe, Math.sin(this.swayPh * 0.7) * 0.08, 0);
    this.head.rotation.set(C.head - breathe * 0.5, 0, 0);
    this.jaw.rotation.set(C.jaw, 0, 0);
    for (const w of this.wings) {
      w.sh.rotation.set(0, w.side * C.fold, w.side * (C.flap + flap));
      w.el.rotation.set(0, w.side * C.elbow, w.side * flap * 0.45);
    }
    for (const l of this.legs) {
      const sw = C.legRate > 0 ? Math.sin(this.walkPh + (l.side > 0 ? 0 : Math.PI)) * C.leg : 0;
      l.hip.rotation.set(sw + C.tuck * 0.95, 0, 0);
      l.knee.rotation.set(-Math.max(0, -sw) * 0.6 - C.tuck * 0.4, 0, 0);
    }
    for (let i = 0; i < this.tail.length; i++) {
      const sway = Math.sin(this.swayPh * 1.6 - i * 0.7) * C.tailSway;
      this.tail[i].rotation.set(i === 0 ? -C.tailLift * 0.6 + 0.15 : 0.05 + C.tailLift * 0.12, C.tailYaw / TAIL_N * 1.6 + sway, 0);
    }
    this.uGlow.value = C.glow;
    this.uThroat.value = C.throat;
    this.uFlame.value = C.flame;
    this.uTime.value = clocks.worldNow / 60;
    this.throatMesh.visible = C.throat > 0.02;
    this.flame.visible = this.breathCone && C.flame > 0.02;
    if (this.flame.visible) this.flame.scale.set(1 + C.flame * 0.6, 1 + C.flame * 0.6, 3 + 9 * C.flame);

    // 영웅이 몸 북쪽(카메라 반대편) 가까이 있어 가려지면 비춰 보이게 한다(공중이면 몸이 높아 가리지 않는다)
    const h = view.hero;
    const hx = h.prevX + (h.x - h.prevX) * alpha, hz = h.prevZ + (h.z - h.prevZ) * alpha;
    const dz = this.sz - hz, dx = Math.abs(this.sx - hx);
    // 비룡은 낮고 넓어서(키 약 4.5m) 영웅이 몸 바로 북쪽(카메라 반대편)에 있을 때만 가린다. 옆·앞에서 싸울 때 비치게 하면 몸이 늘 유령처럼 보였다(2026-10-26 캡처)
    const behind = air < 0.3 && dz > 1.0 && dz < 6.5 && dx < 3.6 ? 1 : 0;
    this.fadeV += (behind - this.fadeV) * damp(10, dt);
    this.fade.value = this.fadeV > 0.02 ? this.fadeV : 0;

    // 피격 흰 플래시(약하게), 공중·등장 무적이면 금빛 림 맥동
    // 피격: 연속 타격이 끊이지 않아 흰 플래시는 몸을 회색으로 바래게 했다(2026-10-26 캡처) — 짧고 따뜻한 빛만 더한다
    const flash = b.hitTick < 2;
    for (const m of this.skinMats) { m.emissive.setHex(flash ? 0xff7a40 : 0x000000); m.emissiveIntensity = flash ? 0.07 : 0; }
    if (b.invulnerable === true) {
      this.rim.color.value.setHex(0xffd36a);
      this.rim.strength.value = 0.85 + 0.45 * Math.sin(clocks.realMs * 0.0088);
    } else {
      this.rim.color.value.setHex(view.raid && view.raid.enraged ? 0xff5030 : 0xffb070);
      this.rim.strength.value = view.raid && view.raid.enraged ? 0.5 + 0.15 * Math.sin(clocks.realMs * 0.006) : 0.32;
    }
  }

  /** 미리 컴파일·QA용 정보 */
  get meshCount(): number { let n = 0; this.root.traverse((o) => { if ((o as THREE.Mesh).isMesh) n++; }); return n; }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
  }
}

